import { useEffect, useMemo, useState } from 'react'
import { ChevronLeft, ChevronRight, Download, Gift } from '@digital-twins/design-system/icons'
import { Button } from '@design-system/components/button'
import { Text } from '@design-system/components/Text'
import type { CreditTransaction, UsageKind } from '@/lib/api'
import { formatUsdFromCents } from './formatMoney'
import {
  downloadCsv,
  rowsToCsv,
  txToUsageRow,
} from './exportUsageCsv'
import { useTranslation } from 'react-i18next'
import { intlLocaleForUi } from '@/i18n'

interface UsageLogTableProps {
  /**
   * The full slice of transactions to render. The table groups consecutive
   * runs and paginates over groups internally, so this should be the largest
   * window the caller is comfortable shipping in one fetch (the backend caps
   * `limit` at 100). CSV export uses every row in this list.
   */
  transactions: CreditTransaction[]
  loading?: boolean
  /** Shown when the API does not resolve an actor (e.g. system rows). */
  viewerFallbackDisplay: string
  /** Logged-in user id (`users.token`) for matching unresolved self rows. */
  currentUserId?: string
  /** Per-credit display rate in cents (overage rate at the user's plan). */
  costPerCreditCents: number
  /** Show the Cost column only when the user has overage charges. */
  showCost?: boolean
}

const MAX_PAGE_BUTTONS = 7
const GROUPS_PER_PAGE = 10

const KIND_BADGE_CLASS: Record<UsageKind, string> = {
  audience:
    'bg-emerald-50 text-emerald-700 dark:bg-emerald-500/15 dark:text-emerald-300',
  study:
    'bg-violet-50 text-violet-700 dark:bg-violet-500/15 dark:text-violet-300',
  chat: 'bg-sky-50 text-sky-700 dark:bg-sky-500/15 dark:text-sky-300',
  // Theme tokens only: readable in both themes without a dark override.
  social: 'bg-chart-accent/15 text-foreground',
  system: 'bg-muted text-muted-foreground',
}

function getPageList(
  currentPage: number,
  totalPages: number,
): Array<number | 'ellipsis'> {
  if (totalPages <= MAX_PAGE_BUTTONS) {
    return Array.from({ length: totalPages }, (_, i) => i + 1)
  }
  const pages: Array<number | 'ellipsis'> = [1]
  const start = Math.max(2, currentPage - 1)
  const end = Math.min(totalPages - 1, currentPage + 1)
  if (start > 2) pages.push('ellipsis')
  for (let p = start; p <= end; p++) pages.push(p)
  if (end < totalPages - 1) pages.push('ellipsis')
  pages.push(totalPages)
  return pages
}

interface UsageGroup {
  /** Most recent (top-most) tx in the run, used as the row's stable key. */
  leader: CreditTransaction
  members: CreditTransaction[]
}

/**
 * Folds consecutive transactions sharing user + kind + object + description
 * into a single row. Singletons stay as one-member groups so the rendering
 * code can be uniform — the `×N` count badge is only shown when N > 1.
 *
 * Grouping happens over the full `transactions` list passed in, *before*
 * pagination, so a group will never be split across pages.
 */
export function groupTransactions(
  transactions: CreditTransaction[],
): UsageGroup[] {
  const groups: UsageGroup[] = []
  for (const tx of transactions) {
    const last = groups[groups.length - 1]
    if (last && isSameRun(last.leader, tx)) {
      last.members.push(tx)
    } else {
      groups.push({ leader: tx, members: [tx] })
    }
  }
  return groups
}

function isSameRun(a: CreditTransaction, b: CreditTransaction): boolean {
  return (
    (a.performedByUserId ?? null) === (b.performedByUserId ?? null) &&
    a.kind === b.kind &&
    (a.objectType ?? null) === (b.objectType ?? null) &&
    (a.objectId ?? null) === (b.objectId ?? null) &&
    a.description === b.description &&
    a.type === b.type
  )
}

function sumSignedCredits(members: CreditTransaction[]): number {
  return members.reduce(
    (acc, m) => acc + (m.type === 'debit' ? m.amount : -m.amount),
    0,
  )
}

export function UsageLogTable(props: UsageLogTableProps) {
  const {
    transactions,
    loading,
    viewerFallbackDisplay,
    currentUserId,
    costPerCreditCents,
    showCost = false,
  } = props
  const { t, i18n } = useTranslation(['app', 'common'])
  const dateLocale = intlLocaleForUi(i18n.language)

  function formatUsageDate(iso: string): string {
    const d = new Date(iso)
    if (Number.isNaN(d.getTime())) return iso
    return d.toLocaleString(dateLocale, {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    })
  }

  const usageRowOpts = {
    viewerFallback: viewerFallbackDisplay,
    currentUserId,
  }

  const groups = useMemo(() => groupTransactions(transactions), [transactions])

  const [page, setPage] = useState(1)
  // Reset to page 1 whenever the underlying data changes (e.g., new fetch
  // after a refund or a cycle reset) so the user isn't stranded on a page
  // that no longer exists.
  useEffect(() => {
    setPage(1)
  }, [transactions])

  const totalGroups = groups.length
  const totalPages = Math.max(1, Math.ceil(totalGroups / GROUPS_PER_PAGE))

  useEffect(() => {
    if (page > totalPages) setPage(totalPages)
  }, [page, totalPages])

  const startIdx = (page - 1) * GROUPS_PER_PAGE
  const visibleGroups = groups.slice(startIdx, startIdx + GROUPS_PER_PAGE)
  const rangeStart = totalGroups === 0 ? 0 : startIdx + 1
  const rangeEnd = Math.min(totalGroups, startIdx + visibleGroups.length)

  function handleExport() {
    // CSV export keeps every individual row — grouping is presentation-only.
    const csv = rowsToCsv(
      transactions.map((tx) =>
        txToUsageRow(tx, costPerCreditCents, usageRowOpts),
      ),
    )
    downloadCsv(`usage-log-${new Date().toISOString().slice(0, 10)}.csv`, csv)
  }

  return (
    <div className="flex flex-col gap-4" data-testid="billing-usage-log">
      <div className="flex items-center justify-between">
        <Text size="sm" weight="medium">
          {t('billing.usageLog')}
        </Text>
        <Button
          variant="outline"
          size="sm"
          onClick={handleExport}
          disabled={transactions.length === 0}
          data-testid="billing-usage-export"
          className="h-9 gap-1.5 rounded-md border-cta px-3 text-sm font-medium text-cta hover:bg-cta/5 dark:border-cta dark:text-cta dark:hover:bg-cta/10"
        >
          <Download className="h-3.5 w-3.5" aria-hidden />
          {t('billing.exportCsv')}
        </Button>
      </div>

      <div className="overflow-x-auto -mx-4 md:mx-0">
        <table className="w-full border-separate border-spacing-0 text-left text-sm min-w-[540px]">
          <thead>
            <tr>
              <th scope="col" className="rounded-l-xl bg-muted/50 px-3 py-3 text-xs font-normal text-muted-foreground">
                {t('billing.colDate')}
              </th>
              <th scope="col" className="bg-muted/50 px-3 py-3 text-xs font-normal text-muted-foreground">
                {t('billing.colUser')}
              </th>
              <th scope="col" className="bg-muted/50 px-3 py-3 text-xs font-normal text-muted-foreground">
                {t('billing.colType')}
              </th>
              <th scope="col" className="bg-muted/50 px-3 py-3 text-xs font-normal text-muted-foreground">
                {t('billing.colName')}
              </th>
              <th scope="col" className={`bg-muted/50 px-3 py-3 text-right text-xs font-normal text-muted-foreground ${!showCost ? 'rounded-r-xl' : ''}`}>
                {t('billing.colCredits')}
              </th>
              {showCost && (
                <th scope="col" className="rounded-r-xl bg-muted/50 px-3 py-3 text-right text-xs font-normal text-muted-foreground">
                  {t('billing.colCost')}
                </th>
              )}
            </tr>
          </thead>
          <tbody data-testid="billing-usage-log-tbody">
            {loading && (
              <tr>
                <td
                  colSpan={6}
                  className="px-3 py-8 text-center text-sm text-muted-foreground"
                >
                  {t('loading', { ns: 'common' })}
                </td>
              </tr>
            )}
            {!loading && visibleGroups.length === 0 && (
              <tr>
                <td
                  colSpan={showCost ? 6 : 5}
                  className="px-3 py-8 text-center text-sm text-muted-foreground"
                >
                  {t('billing.noUsage')}
                </td>
              </tr>
            )}
            {!loading &&
              visibleGroups.map((group, groupIdx) => {
                const leader = group.leader
                const leaderRow = txToUsageRow(
                  leader,
                  costPerCreditCents,
                  usageRowOpts,
                )
                const isGroup = group.members.length > 1
                const credits = isGroup
                  ? sumSignedCredits(group.members)
                  : leaderRow.credits
                const costUsd = (credits * costPerCreditCents) / 100
                const isLastRow = groupIdx === visibleGroups.length - 1
                const cellBorder = isLastRow ? '' : 'border-b border-border/40 dark:border-border'

                return (
                  <tr
                    key={leader.id}
                    data-testid={
                      isGroup ? 'billing-usage-group-row' : 'billing-usage-row'
                    }
                  >
                    <td className={`whitespace-nowrap px-3 py-3.5 text-muted-foreground ${cellBorder}`}>
                      {formatUsageDate(leaderRow.date)}
                    </td>
                    <td className={`px-3 py-3.5 font-medium text-foreground ${cellBorder}`}>
                      {leaderRow.user}
                    </td>
                    <td className={`px-3 py-3.5 ${cellBorder}`}>
                      <span
                        className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${KIND_BADGE_CLASS[leader.kind]}`}
                        data-testid={`billing-usage-kind-${leader.kind}`}
                      >
                        {t(`billing.kind.${leader.kind}`)}
                      </span>
                    </td>
                    <td className={`px-3 py-3.5 truncate text-foreground ${cellBorder}`}>
                      {leader.bonusSource ? (
                        <span className="inline-flex items-center gap-1.5">
                          <Gift className="h-3.5 w-3.5 shrink-0 text-amber-600 dark:text-amber-400" aria-hidden />
                          <span>{
                            leader.bonusSource === 'referral_sharer'
                              ? t('billing.bonusReferralShared')
                              : leader.bonusSource === 'referral_signup'
                                ? t('billing.bonusReferralSignup')
                                : leader.bonusSource === 'guest_chat_creditback'
                                  ? t('billing.bonusGuestChat')
                                  : leaderRow.project
                          }</span>
                        </span>
                      ) : (
                        <span>{leaderRow.project}</span>
                      )}
                      {isGroup && (
                        <span
                          className="ml-2 inline-flex items-center rounded-full bg-muted px-2 py-0.5 text-xs font-medium text-muted-foreground"
                          data-testid="billing-usage-group-count"
                          aria-label={t('billing.groupedAria', { count: group.members.length })}
                        >
                          ×{group.members.length}
                        </span>
                      )}
                    </td>
                    <td className={`px-3 py-3.5 text-right tabular-nums text-foreground ${cellBorder}`}>
                      {Number.isInteger(credits) ? credits : credits.toFixed(2)}
                    </td>
                    {showCost && (
                      <td className={`px-3 py-3.5 text-right font-medium tabular-nums text-foreground ${cellBorder}`}>
                        {costUsd > 0
                          ? formatUsdFromCents(Math.round(costUsd * 100))
                          : <span className="text-muted-foreground">—</span>}
                      </td>
                    )}
                  </tr>
                )
              })}
          </tbody>
        </table>
      </div>

      {totalGroups > GROUPS_PER_PAGE && (
        <div
          className="flex flex-col items-center justify-between gap-2 sm:flex-row"
          data-testid="billing-usage-pagination"
        >
          <Text size="xs" color="muted">
            {t('billing.showingRange', { start: rangeStart, end: rangeEnd, total: totalGroups })}
          </Text>
          <div className="flex items-center gap-1">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setPage(Math.max(1, page - 1))}
              disabled={page === 1 || loading}
              aria-label={t('billing.prevPage')}
              data-testid="billing-usage-page-prev"
              className="h-8 w-8 p-0"
            >
              <ChevronLeft className="h-4 w-4" aria-hidden />
            </Button>
            {getPageList(page, totalPages).map((p, i) =>
              p === 'ellipsis' ? (
                <span
                  key={`e-${i}`}
                  className="px-2 text-sm text-muted-foreground"
                  aria-hidden
                >
                  …
                </span>
              ) : (
                <Button
                  key={p}
                  variant={p === page ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setPage(p)}
                  disabled={loading}
                  aria-label={t('billing.pageAria', { page: p })}
                  aria-current={p === page ? 'page' : undefined}
                  data-testid={`billing-usage-page-${p}`}
                  className="h-8 min-w-8 px-2 tabular-nums"
                >
                  {p}
                </Button>
              ),
            )}
            <Button
              variant="outline"
              size="sm"
              onClick={() => setPage(Math.min(totalPages, page + 1))}
              disabled={page === totalPages || loading}
              aria-label={t('billing.nextPage')}
              data-testid="billing-usage-page-next"
              className="h-8 w-8 p-0"
            >
              <ChevronRight className="h-4 w-4" aria-hidden />
            </Button>
          </div>
        </div>
      )}
    </div>
  )
}
